import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import Select from "react-select";
import api from "api";
import PageHeader from "components/layout/page-header/PageHeader";
import AppModal from "components/ui/AppModal/AppModal";
import styles from "./AdminPainelControle.module.css";

const PAGE_SIZE = 50;

function prefixToCrumbs(prefix) {
  const crumbs = [{ label: "Raiz", prefix: "" }];
  if (!prefix) return crumbs;
  const parts = prefix.replace(/\/$/, "").split("/").filter(Boolean);
  let acc = "";
  for (const p of parts) {
    acc = acc ? `${acc}/${p}/` : `${p}/`;
    crumbs.push({ label: p, prefix: acc });
  }
  return crumbs;
}

function formatBytes(n) {
  if (n == null || Number.isNaN(n)) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function folderDisplayName(fullPath, currentPrefix) {
  let rel = fullPath || "";
  const cp = currentPrefix || "";
  if (cp && rel.startsWith(cp)) rel = rel.slice(cp.length);
  rel = rel.replace(/\/$/, "");
  const seg = rel.split("/").filter(Boolean).pop();
  return seg || rel || fullPath;
}

function formatCellValue(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

function AdminPainelControle({ token }) {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [error, setError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [dataSource, setDataSource] = useState("postgres");
  const [azureContainers, setAzureContainers] = useState([]);
  const [azureContainer, setAzureContainer] = useState(null);
  const [azurePrefix, setAzurePrefix] = useState("");
  const [azureBrowse, setAzureBrowse] = useState(null);
  const [azureLoading, setAzureLoading] = useState(false);
  const [azureRefreshTick, setAzureRefreshTick] = useState(0);
  const [azureReplaceBlobName, setAzureReplaceBlobName] = useState(null);
  const [azureOpBusy, setAzureOpBusy] = useState(false);
  const azureFileReplaceRef = useRef(null);

  const [editor, setEditor] = useState({ open: false, values: {}, sourceRow: null });
  const [saving, setSaving] = useState(false);

  const [modal, setModal] = useState({ open: false, variant: "warning", title: "", message: "", onConfirm: null });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const modalResolveRef = useRef(null);
  const selectAllCheckboxRef = useRef(null);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const openModal = config =>
    new Promise(resolve => {
      modalResolveRef.current = resolve;
      setModal({ ...config, open: true });
    });

  const closeModal = () => {
    setModal(m => ({ ...m, open: false }));
    modalResolveRef.current?.();
    modalResolveRef.current = null;
  };

  useEffect(() => {
    if (!token) return;
    api
      .get("/admin/db/tables", { headers: authHeaders })
      .then(({ data }) => setTables(Array.isArray(data?.tables) ? data.tables : []))
      .catch(() => setTables([]));
  }, [token, authHeaders]);

  useEffect(() => {
    if (!token || dataSource !== "azure") return;
    api
      .get("/admin/storage/containers", { headers: authHeaders })
      .then(({ data }) => setAzureContainers(Array.isArray(data?.containers) ? data.containers : []))
      .catch(() => setAzureContainers([]));
  }, [token, authHeaders, dataSource]);

  useEffect(() => {
    setAzurePrefix("");
  }, [azureContainer?.name]);

  useEffect(() => {
    if (!token || dataSource !== "azure" || !azureContainer?.name) {
      setAzureBrowse(null);
      return;
    }
    let cancelled = false;
    setAzureLoading(true);
    setError(null);
    api
      .get("/admin/storage/browse", {
        headers: authHeaders,
        params: { container: azureContainer.name, prefix: azurePrefix || undefined }
      })
      .then(({ data }) => {
        if (!cancelled) setAzureBrowse(data);
      })
      .catch(e => {
        if (!cancelled) {
          setAzureBrowse(null);
          setError(e.response?.data?.detail || e.message || "Erro ao listar o storage.");
        }
      })
      .finally(() => {
        if (!cancelled) setAzureLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, authHeaders, dataSource, azureContainer?.name, azurePrefix, azureRefreshTick]);

  const loadMeta = useCallback(
    async tableName => {
      if (!tableName || !token) return;
      setLoadingMeta(true);
      setError(null);
      try {
        const { data } = await api.get(`/admin/db/tables/${tableName}/meta`, { headers: authHeaders });
        setMeta(data);
      } catch (e) {
        setMeta(null);
        setError(e.response?.data?.detail || e.message || "Erro ao carregar metadados.");
      } finally {
        setLoadingMeta(false);
      }
    },
    [token, authHeaders]
  );

  const loadRows = useCallback(
    async (tableName, off = 0, sortOpts = {}) => {
      if (!tableName || !token) return;
      setLoadingList(true);
      setError(null);
      const params = { limit: PAGE_SIZE, offset: off };
      if (sortOpts.sort) {
        params.sort = sortOpts.sort;
        params.order = sortOpts.order || "asc";
      }
      try {
        const { data } = await api.get(`/admin/db/tables/${tableName}/rows`, {
          headers: authHeaders,
          params
        });
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(typeof data?.total === "number" ? data.total : 0);
        setOffset(off);
      } catch (e) {
        setRows([]);
        setTotal(0);
        setError(e.response?.data?.detail || e.message || "Erro ao carregar dados.");
      } finally {
        setLoadingList(false);
      }
    },
    [token, authHeaders]
  );

  const sortParams = useMemo(
    () => (sortColumn ? { sort: sortColumn, order: sortDir } : {}),
    [sortColumn, sortDir]
  );

  useEffect(() => {
    setSelectedRowKeys([]);
    setSortColumn(null);
    setSortDir("asc");
    if (!selectedTable?.name) {
      setMeta(null);
      setRows([]);
      setTotal(0);
      return;
    }
    loadMeta(selectedTable.name);
    loadRows(selectedTable.name, 0, {});
  }, [selectedTable, loadMeta, loadRows]);

  const handleSortClick = cn => {
    if (!selectedTable?.name) return;
    const same = sortColumn === cn;
    const nextDir = same ? (sortDir === "asc" ? "desc" : "asc") : "asc";
    if (same) {
      setSortDir(nextDir);
    } else {
      setSortColumn(cn);
      setSortDir("asc");
    }
    loadRows(selectedTable.name, 0, { sort: cn, order: same ? nextDir : "asc" });
  };

  const tableOptions = useMemo(
    () => tables.map(t => ({ value: t.name, label: `${t.label} (${t.name})` })),
    [tables]
  );

  const sourceOptions = useMemo(
    () => [
      { value: "postgres", label: "PostgreSQL (banco)" },
      { value: "azure", label: "Azure Blob Storage" }
    ],
    []
  );

  const azureContainerOptions = useMemo(
    () => azureContainers.map(c => ({ value: c.name, label: c.name })),
    [azureContainers]
  );

  const handleDataSourceChange = opt => {
    const v = opt?.value ?? "postgres";
    setError(null);
    setDataSource(v);
    if (v === "azure") {
      setSelectedTable(null);
      setMeta(null);
      setRows([]);
      setTotal(0);
      setSelectedRowKeys([]);
      setSortColumn(null);
      setSortDir("asc");
    } else {
      setAzureContainer(null);
      setAzurePrefix("");
      setAzureBrowse(null);
      setAzureRefreshTick(0);
    }
  };

  /** Portal + z-index alto: cabeçalho sticky da tabela não cobre o menu do Select. */
  const reactSelectStyles = useMemo(
    () => ({
      menuPortal: base => ({ ...base, zIndex: 10001 }),
      menu: base => ({ ...base, zIndex: 10001 })
    }),
    []
  );

  const columnNames = useMemo(() => {
    if (!meta?.columns?.length) return [];
    return meta.columns.map(c => c.name);
  }, [meta]);

  const openEdit = row => {
    const values = {};
    (meta?.columns || []).forEach(c => {
      const v = row[c.name];
      if (c.type === "json" && v !== null && v !== undefined) {
        values[c.name] = typeof v === "string" ? v : JSON.stringify(v, null, 2);
      } else if (c.type === "boolean") {
        values[c.name] = Boolean(v);
      } else {
        values[c.name] = v === null || v === undefined ? "" : v;
      }
    });
    if (selectedTable?.name === "users") {
      values.plain_password = "";
    }
    setEditor({ open: true, values, sourceRow: row });
  };

  const closeEditor = () => setEditor({ open: false, values: {}, sourceRow: null });

  const buildPk = row => {
    const pk = {};
    (meta?.primary_key || []).forEach(k => {
      pk[k] = row[k];
    });
    return pk;
  };

  /** Chave estável para seleção / comparação (PK ordenada por nome de coluna). */
  const rowSelectionKey = useCallback(
    row => {
      const pk = {};
      (meta?.primary_key || []).forEach(k => {
        pk[k] = row[k];
      });
      const keys = Object.keys(pk).sort();
      return JSON.stringify(keys.reduce((acc, k) => ({ ...acc, [k]: pk[k] }), {}));
    },
    [meta]
  );

  const selectedSet = useMemo(() => new Set(selectedRowKeys), [selectedRowKeys]);

  const toggleRowSelected = row => {
    const k = rowSelectionKey(row);
    setSelectedRowKeys(prev => (prev.includes(k) ? prev.filter(x => x !== k) : [...prev, k]));
  };

  const pageRowKeys = useMemo(() => rows.map(r => rowSelectionKey(r)), [rows, rowSelectionKey]);
  const allPageSelected = pageRowKeys.length > 0 && pageRowKeys.every(k => selectedSet.has(k));
  const somePageSelected = pageRowKeys.some(k => selectedSet.has(k));

  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (el) el.indeterminate = somePageSelected && !allPageSelected;
  }, [somePageSelected, allPageSelected]);

  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      setSelectedRowKeys(prev => prev.filter(k => !pageRowKeys.includes(k)));
    } else {
      setSelectedRowKeys(prev => [...new Set([...prev, ...pageRowKeys])]);
    }
  };

  const handleDeleteSelected = async () => {
    if (!selectedTable?.name || selectedRowKeys.length === 0) return;
    const n = selectedRowKeys.length;
    if (!window.confirm(`Excluir ${n} registro(s) selecionado(s)? Esta ação não pode ser desfeita.`)) return;
    const failures = [];
    for (const keyStr of selectedRowKeys) {
      try {
        const pk = JSON.parse(keyStr);
        await api.delete(`/admin/db/tables/${selectedTable.name}/rows`, {
          headers: authHeaders,
          data: { pk }
        });
      } catch (e) {
        const msg = e.response?.data?.detail || e.message || "Erro";
        failures.push(String(msg));
      }
    }
    setSelectedRowKeys([]);
    await loadRows(selectedTable.name, offset, sortParams);
    if (failures.length > 0) {
      await openModal({
        variant: "error",
        title: "Algumas exclusões falharam",
        message: `${failures.length} de ${n}: ${failures.slice(0, 3).join("; ")}${failures.length > 3 ? "…" : ""}`,
        onConfirm: closeModal
      });
    }
  };

  const parsePayloadValues = () => {
    const out = {};
    const cols = meta?.columns || [];
    for (const col of cols) {
      let raw = editor.values[col.name];
      if (raw === "" || raw === undefined) {
        if (col.nullable) out[col.name] = null;
        else continue;
      } else if (col.type === "json") {
        try {
          out[col.name] = typeof raw === "string" ? JSON.parse(raw) : raw;
        } catch {
          throw new Error(`JSON inválido no campo ${col.name}`);
        }
      } else if (col.type === "integer") {
        out[col.name] = parseInt(String(raw), 10);
      } else if (col.type === "float") {
        out[col.name] = parseFloat(String(raw));
      } else if (col.type === "boolean") {
        out[col.name] = Boolean(raw);
      } else {
        out[col.name] = raw;
      }
    }
    if (selectedTable?.name === "users") {
      const pw = editor.values.plain_password;
      if (pw && String(pw).trim() !== "") {
        out.plain_password = String(pw);
      }
    }
    return out;
  };

  const handleSave = async () => {
    if (!selectedTable?.name || !meta || !editor.sourceRow) return;
    setSaving(true);
    setError(null);
    try {
      const payload = parsePayloadValues();
      const pk = buildPk(editor.sourceRow);
      await api.put(
        `/admin/db/tables/${selectedTable.name}/rows`,
        { pk, values: payload },
        { headers: authHeaders }
      );
      await loadRows(selectedTable.name, offset, sortParams);
      closeEditor();
      await openModal({
        variant: "success",
        title: "Salvo",
        message: "Registro atualizado.",
        onConfirm: closeModal
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Falha ao salvar.";
      if (typeof msg === "object") {
        setError(JSON.stringify(msg));
      } else {
        setError(msg);
      }
      await openModal({ variant: "error", title: "Erro", message: String(msg), onConfirm: closeModal });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async row => {
    if (!window.confirm("Excluir este registro? Esta ação não pode ser desfeita.")) return;
    try {
      const pk = buildPk(row);
      await api.delete(`/admin/db/tables/${selectedTable.name}/rows`, {
        headers: authHeaders,
        data: { pk }
      });
      setSelectedRowKeys(prev => prev.filter(k => k !== rowSelectionKey(row)));
      await loadRows(selectedTable.name, offset, sortParams);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Falha ao excluir.";
      await openModal({ variant: "error", title: "Erro", message: String(msg), onConfirm: closeModal });
    }
  };

  const handleAzureDeleteBlob = async blobName => {
    if (!azureContainer?.name || !blobName) return;
    const prefix = azureBrowse?.prefix ?? "";
    const short = folderDisplayName(blobName, prefix);
    if (!window.confirm(`Excluir o arquivo "${short}"? Esta ação não pode ser desfeita.`)) return;
    setAzureOpBusy(true);
    setError(null);
    try {
      await api.delete("/admin/storage/blob", {
        headers: authHeaders,
        data: { container: azureContainer.name, blob: blobName }
      });
      setAzureRefreshTick(t => t + 1);
      await openModal({
        variant: "success",
        title: "Arquivo excluído",
        message: short,
        onConfirm: closeModal
      });
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "Falha ao excluir.";
      await openModal({ variant: "error", title: "Erro", message: String(msg), onConfirm: closeModal });
    } finally {
      setAzureOpBusy(false);
    }
  };

  const handleAzureReplacePick = blobName => {
    setAzureReplaceBlobName(blobName);
    requestAnimationFrame(() => azureFileReplaceRef.current?.click());
  };

  const renderFormField = col => {
    const name = col.name;
    const v = editor.values[name];
    const disabledPkOnEdit = col.primary_key;

    if (col.type === "boolean") {
      return (
        <label className={styles.formGroup} key={name}>
          <span className={styles.formLabel}>{name}</span>
          <input
            type="checkbox"
            checked={Boolean(v)}
            disabled={disabledPkOnEdit}
            onChange={e => setEditor(ed => ({ ...ed, values: { ...ed.values, [name]: e.target.checked } }))}
          />
        </label>
      );
    }

    if (col.type === "json") {
      return (
        <label className={styles.formGroup} key={name}>
          <span className={styles.formLabel}>{name} (JSON)</span>
          <textarea
            className={styles.formInput}
            rows={4}
            disabled={disabledPkOnEdit}
            value={v ?? ""}
            onChange={e => setEditor(ed => ({ ...ed, values: { ...ed.values, [name]: e.target.value } }))}
          />
        </label>
      );
    }

    const inputType =
      col.type === "integer" || col.type === "float"
        ? "number"
        : col.type === "datetime" || col.type === "date"
          ? "text"
          : "text";

    return (
      <label className={styles.formGroup} key={name}>
        <span className={styles.formLabel}>
          {name}
          {col.nullable ? " (opcional)" : ""}
        </span>
        <input
          className={styles.formInput}
          type={inputType}
          disabled={disabledPkOnEdit}
          placeholder={col.type === "datetime" ? "ISO 8601" : col.type === "date" ? "YYYY-MM-DD" : ""}
          value={v ?? ""}
          onChange={e => setEditor(ed => ({ ...ed, values: { ...ed.values, [name]: e.target.value } }))}
        />
      </label>
    );
  };

  const showPrev = offset > 0;
  const showNext = offset + PAGE_SIZE < total;

  return (
    <div className={styles.pageContainer}>
      <PageHeader
        title="Painel de Controle"
        subtitle="PostgreSQL: tabelas e CRUD. Azure Blob: pastas; substituir ou excluir arquivos (sem edição de texto no blob)."
        accent="Administração"
      />

      <input
        ref={azureFileReplaceRef}
        type="file"
        className={styles.srOnlyFile}
        tabIndex={-1}
        aria-hidden="true"
        onChange={async e => {
          const file = e.target.files?.[0];
          const blobName = azureReplaceBlobName;
          e.target.value = "";
          setAzureReplaceBlobName(null);
          if (!file || !blobName || !azureContainer?.name || !token) return;
          setAzureOpBusy(true);
          setError(null);
          try {
            const fd = new FormData();
            fd.append("container", azureContainer.name);
            fd.append("blob", blobName);
            fd.append("file", file);
            await api.put("/admin/storage/blob", fd, { headers: authHeaders });
            setAzureRefreshTick(t => t + 1);
            await openModal({
              variant: "success",
              title: "Arquivo substituído",
              message: folderDisplayName(blobName, azureBrowse?.prefix ?? ""),
              onConfirm: closeModal
            });
          } catch (err) {
            const msg = err.response?.data?.detail || err.message || "Falha ao enviar.";
            await openModal({ variant: "error", title: "Erro", message: String(msg), onConfirm: closeModal });
          } finally {
            setAzureOpBusy(false);
          }
        }}
      />

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.toolbar}>
        <div className={styles.sourceSelectWrap}>
          <Select
            classNamePrefix="react-select"
            options={sourceOptions}
            value={sourceOptions.find(o => o.value === dataSource) || null}
            onChange={handleDataSourceChange}
            isDisabled={!token}
            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            menuPosition="fixed"
            styles={reactSelectStyles}
            placeholder="Fonte de dados..."
          />
        </div>
        {dataSource === "postgres" && (
          <div className={styles.selectWrap}>
            <Select
              classNamePrefix="react-select"
              placeholder="Selecione uma tabela..."
              isClearable
              options={tableOptions}
              value={tableOptions.find(o => o.value === selectedTable?.name) || null}
              onChange={opt => setSelectedTable(opt ? { name: opt.value, label: opt.label } : null)}
              isDisabled={!token}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={reactSelectStyles}
            />
          </div>
        )}
        {dataSource === "azure" && (
          <div className={styles.selectWrap}>
            <Select
              classNamePrefix="react-select"
              placeholder="Selecione um container..."
              isClearable
              options={azureContainerOptions}
              value={azureContainerOptions.find(o => o.value === azureContainer?.name) || null}
              onChange={opt => setAzureContainer(opt ? { name: opt.value } : null)}
              isDisabled={!token}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              styles={reactSelectStyles}
            />
          </div>
        )}
        <div className={styles.actions}>
          {dataSource === "postgres" && (
            <>
              <button
                type="button"
                className={styles.btnDangerOutline}
                disabled={!selectedTable || selectedRowKeys.length === 0 || loadingList}
                onClick={handleDeleteSelected}
                title="Excluir linhas marcadas na página ou em outras páginas"
              >
                <i className="fas fa-trash-alt" aria-hidden="true" />
                <span> Excluir selecionados ({selectedRowKeys.length})</span>
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={!selectedTable || loadingList}
                onClick={() => selectedTable && loadRows(selectedTable.name, offset, sortParams)}
              >
                Atualizar
              </button>
            </>
          )}
          {dataSource === "azure" && (
            <button
              type="button"
              className={styles.btnSecondary}
              disabled={!azureContainer || azureLoading || azureOpBusy}
              onClick={() => setAzureRefreshTick(t => t + 1)}
            >
              Atualizar
            </button>
          )}
        </div>
      </div>

      <div className={styles.card}>
        {dataSource === "postgres" && !selectedTable && (
          <div className={styles.emptyState}>Escolha uma tabela para carregar os dados.</div>
        )}
        {dataSource === "postgres" && selectedTable && loadingList && !rows.length && (
          <div className={styles.emptyState}>Carregando…</div>
        )}
        {dataSource === "postgres" && selectedTable && !loadingList && !columnNames.length && !loadingMeta && (
          <div className={styles.emptyState}>Não foi possível carregar as colunas.</div>
        )}
        {dataSource === "postgres" && selectedTable && columnNames.length > 0 && (
          <>
            <div className={styles.tableScroll}>
              {selectedTable && (
                <div className={styles.tableMetaBar} aria-live="polite">
                  <span className={styles.tableMetaName}>{selectedTable.name}</span>
                  <span className={styles.tableMetaCount}>
                    {total} {total === 1 ? "registro" : "registros"}
                  </span>
                </div>
              )}
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th className={styles.thCheckbox} title="Selecionar nesta página">
                      <input
                        ref={selectAllCheckboxRef}
                        type="checkbox"
                        className={styles.selectCheckbox}
                        checked={allPageSelected}
                        onChange={toggleSelectAllPage}
                        aria-label="Selecionar ou limpar todas as linhas desta página"
                      />
                    </th>
                    <th className={styles.thActions}>Ações</th>
                    {columnNames.map(cn => (
                      <th
                        key={cn}
                        className={`${styles.thData} ${sortColumn === cn ? styles.thSorted : ""}`}
                        aria-sort={
                          sortColumn === cn ? (sortDir === "asc" ? "ascending" : "descending") : undefined
                        }
                      >
                        <button
                          type="button"
                          className={styles.thSortBtn}
                          onClick={() => handleSortClick(cn)}
                          title={`Ordenar por ${cn}`}
                        >
                          <span className={styles.thSortLabel}>{cn}</span>
                          <span className={styles.thSortIcon} aria-hidden="true">
                            {sortColumn === cn ? (
                              sortDir === "asc" ? (
                                <i className="fas fa-sort-up" />
                              ) : (
                                <i className="fas fa-sort-down" />
                              )
                            ) : (
                              <i className="fas fa-sort" />
                            )}
                          </span>
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => {
                    const rowKey = rowSelectionKey(row);
                    return (
                    <tr
                      key={rowKey || String(idx)}
                      className={selectedSet.has(rowKey) ? styles.rowSelected : undefined}
                    >
                      <td className={styles.tdCheckbox}>
                        <input
                          type="checkbox"
                          className={styles.selectCheckbox}
                          checked={selectedSet.has(rowKey)}
                          onChange={() => toggleRowSelected(row)}
                          aria-label="Selecionar linha para exclusão em lote"
                        />
                      </td>
                      <td className={styles.tdActions}>
                        <div className={styles.actionCellInner}>
                          <button
                            type="button"
                            className={styles.iconBtn}
                            onClick={() => openEdit(row)}
                            title="Editar"
                            aria-label="Editar registro"
                          >
                            <i className="fas fa-pen" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            onClick={() => handleDelete(row)}
                            title="Excluir"
                            aria-label="Excluir registro"
                          >
                            <i className="fas fa-trash-alt" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                      {columnNames.map(cn => {
                        const display = formatCellValue(row[cn]);
                        const redacted = row[`_redacted_${cn}`];
                        const textFull = !redacted && display != null ? String(display) : "";
                        const showTitle = textFull.length > 72;
                        return (
                          <td key={cn} className={styles.tdData}>
                            {redacted ? (
                              <span className={styles.cellMuted}>(oculto)</span>
                            ) : display === null ? (
                              <span className={styles.cellMuted}>—</span>
                            ) : (
                              <span className={styles.cellContent} title={showTitle ? textFull : undefined}>
                                {display}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>
            {rows.length === 0 && !loadingList && <div className={styles.emptyState}>Nenhum registro nesta página.</div>}
            <div className={styles.pagination}>
              <span>
                {total === 0 ? "0" : `${offset + 1}–${Math.min(offset + rows.length, total)}`} de {total}
              </span>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={!showPrev || loadingList}
                onClick={() =>
                  selectedTable && loadRows(selectedTable.name, Math.max(0, offset - PAGE_SIZE), sortParams)
                }
              >
                Anterior
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                disabled={!showNext || loadingList}
                onClick={() => selectedTable && loadRows(selectedTable.name, offset + PAGE_SIZE, sortParams)}
              >
                Próxima
              </button>
            </div>
          </>
        )}

        {dataSource === "azure" && !azureContainer && (
          <div className={styles.emptyState}>Selecione um container para listar pastas e arquivos.</div>
        )}
        {dataSource === "azure" && azureContainer && azureLoading && (
          <div className={styles.emptyState}>Carregando…</div>
        )}
        {dataSource === "azure" && azureContainer && !azureLoading && azureBrowse && (
          <>
            <div className={styles.tableMetaBar} aria-live="polite">
              <span className={styles.tableMetaName}>{azureBrowse.container}</span>
              <span className={styles.tableMetaCount}>Azure Blob</span>
            </div>
            <nav className={styles.azureBreadcrumbs} aria-label="Caminho">
              {prefixToCrumbs(azureBrowse.prefix ?? "").map((crumb, i) => (
                <span key={crumb.prefix || "root"} className={styles.azureCrumbItem}>
                  {i > 0 && <span className={styles.azureCrumbSep} aria-hidden>/</span>}
                  <button
                    type="button"
                    className={styles.azureCrumbBtn}
                    onClick={() => setAzurePrefix(crumb.prefix)}
                  >
                    {crumb.label}
                  </button>
                </span>
              ))}
            </nav>
            <div className={styles.tableScroll}>
              <table className={styles.dataTable}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Tipo</th>
                    <th>Tamanho</th>
                    <th>Última alteração</th>
                    <th className={`${styles.thActions} ${styles.thAzureActions}`}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {(azureBrowse.folders || []).map(f => (
                    <tr key={f.path}>
                      <td>
                        <button
                          type="button"
                          className={styles.azureFolderLink}
                          onClick={() => setAzurePrefix(f.path)}
                          disabled={azureOpBusy}
                        >
                          <i className="fas fa-folder" aria-hidden="true" />{" "}
                          {folderDisplayName(f.path, azureBrowse.prefix)}
                        </button>
                      </td>
                      <td>Pasta</td>
                      <td>
                        <span className={styles.cellMuted}>—</span>
                      </td>
                      <td>
                        <span className={styles.cellMuted}>—</span>
                      </td>
                      <td className={`${styles.tdActions} ${styles.tdAzureActions}`}>
                        <span className={styles.cellMuted}>—</span>
                      </td>
                    </tr>
                  ))}
                  {(azureBrowse.blobs || []).map(b => (
                    <tr key={b.name}>
                      <td>
                        <span className={styles.azureBlobName} title={b.name}>
                          <i className="fas fa-file" aria-hidden="true" />{" "}
                          {folderDisplayName(b.name, azureBrowse.prefix)}
                        </span>
                      </td>
                      <td>Arquivo</td>
                      <td>{formatBytes(b.size)}</td>
                      <td>
                        {b.last_modified
                          ? new Date(b.last_modified).toLocaleString("pt-BR")
                          : "—"}
                      </td>
                      <td className={`${styles.tdActions} ${styles.tdAzureActions}`}>
                        <div className={styles.actionCellInner}>
                          <button
                            type="button"
                            className={styles.iconBtn}
                            disabled={azureOpBusy}
                            onClick={() => handleAzureReplacePick(b.name)}
                            title="Substituir arquivo (mesmo nome)"
                            aria-label="Substituir arquivo"
                          >
                            <i className="fas fa-upload" aria-hidden="true" />
                          </button>
                          <button
                            type="button"
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            disabled={azureOpBusy}
                            onClick={() => handleAzureDeleteBlob(b.name)}
                            title="Excluir blob"
                            aria-label="Excluir arquivo"
                          >
                            <i className="fas fa-trash-alt" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {(azureBrowse.folders || []).length === 0 && (azureBrowse.blobs || []).length === 0 && (
              <div className={styles.emptyState}>Esta pasta está vazia.</div>
            )}
          </>
        )}
        {dataSource === "azure" && azureContainer && !azureLoading && !azureBrowse && !error && (
          <div className={styles.emptyState}>Não foi possível carregar o conteúdo.</div>
        )}
      </div>

      <AppModal
        isOpen={modal.open}
        onClose={closeModal}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        confirmText="OK"
        onConfirm={modal.onConfirm || closeModal}
      />

      <AppModal
        isOpen={editor.open}
        onClose={closeEditor}
        variant="custom"
        title="Editar registro"
        message={selectedTable ? `Tabela: ${selectedTable.name}` : ""}
        showCloseBtn
        allowOutsideClick
      >
        <div className={styles.formGrid}>
          {(meta?.columns || []).map(col => renderFormField(col))}
          {selectedTable?.name === "users" && (
            <label className={styles.formGroup}>
              <span className={styles.formLabel}>Nova senha (opcional)</span>
              <input
                type="password"
                className={styles.formInput}
                autoComplete="new-password"
                value={editor.values.plain_password ?? ""}
                onChange={e =>
                  setEditor(ed => ({ ...ed, values: { ...ed.values, plain_password: e.target.value } }))
                }
              />
              <p className={styles.formHint}>A senha é armazenada com hash; não use hashed_password no formulário.</p>
            </label>
          )}
          {(meta?.hints || []).map(h => (
            <p key={h.field} className={styles.formHint}>
              {h.note}
            </p>
          ))}
        </div>
        <div className={styles.modalActions}>
          <button type="button" className={styles.btnSecondary} onClick={closeEditor} disabled={saving}>
            Cancelar
          </button>
          <button type="button" className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
            {saving ? "Salvando…" : "Salvar"}
          </button>
        </div>
      </AppModal>
    </div>
  );
}

const mapStateToProps = state => ({
  token: state.auth?.accessToken ?? null
});

export default connect(mapStateToProps)(AdminPainelControle);
